import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { BackLayer, useBackHandler } from '../lib/useBack'

const GAP = 6

interface PopoverProps {
  anchor: RefObject<HTMLElement | null>
  onClose: () => void
  label: string
  width?: number
  children: ReactNode
}

/**
 * Всплывашка у кнопки (компьютер): ♥ избранного, «В контент-план», «⋯» карточки
 * плана. Встаёт верхним слоем стека «Назад»: Esc закрывает её, а не карточку под
 * ней. Закрывается щелчком мимо, прокруткой снаружи и сменой размера окна.
 */
export function Popover({ anchor, onClose, label, width = 288, children }: PopoverProps) {
  const panel = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null)
  const layer = useBackHandler(() => {
    onClose()
    anchor.current?.focus()
  })

  useLayoutEffect(() => {
    const a = anchor.current?.getBoundingClientRect()
    if (!a) return
    const left = Math.max(8, Math.min(a.right - width, window.innerWidth - width - 8))
    const below = window.innerHeight - a.bottom - GAP - 8
    const above = a.top - GAP - 8
    if (below >= 260 || below >= above) setPos({ top: a.bottom + GAP, left, maxHeight: below })
    else setPos({ top: Math.max(8, a.top - GAP - Math.min(above, 420)), left, maxHeight: Math.min(above, 420) })
  }, [anchor, width])

  // Фокус внутрь — со списком можно работать с клавиатуры.
  useEffect(() => {
    if (pos) panel.current?.focus()
  }, [pos])

  useEffect(() => {
    const inside = (t: EventTarget | null) =>
      t instanceof Node && (panel.current?.contains(t) || anchor.current?.contains(t))
    const onDown = (e: PointerEvent) => {
      if (!inside(e.target)) onClose()
    }
    const onScroll = (e: Event) => {
      if (!inside(e.target)) onClose()
    }
    window.addEventListener('pointerdown', onDown, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onClose)
    return () => {
      window.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onClose)
    }
  }, [anchor, onClose])

  return createPortal(
    <BackLayer.Provider value={layer}>
      <div
        ref={panel}
        role="dialog"
        aria-label={label}
        tabIndex={-1}
        style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width, maxHeight: pos?.maxHeight }}
        className="fixed z-50 flex flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-line outline-none"
      >
        {children}
      </div>
    </BackLayer.Provider>,
    document.body,
  )
}
